// ----------------------------------------------------------------------------
// webrtc.duck.js
//
// The "Viveka Video Call" built-in option no longer runs an embedded call
// inside this app. It runs entirely in a separate standalone app ("Viveka
// Meet", live at REACT_APP_MEET_APP_URL / live.viveka.world), forked from
// LiveKit's own official example. This duck's job shrinks to exactly what
// this app is still responsible for: the real-time ring/answer/reject
// handshake (so the other participant gets notified live, the same way it
// always has), then handing off to the separate app by opening it in its own
// new tab (window.open, no 'noopener' -- keeping window.opener alive is what
// lets Viveka Meet post the AI call summary back to us, see
// SaaSActiveSessionPage.js's handleMeetSummaryMessage and that app's
// lib/AutoCallSummary.tsx).
//
// What used to live here (RTCPeerConnection/LiveKit Room management, track
// publishing, background effects, screen share, in-call chat, call-quality
// telemetry) is gone -- none of it applies once the call itself happens in
// a different application entirely.
// ----------------------------------------------------------------------------

import axios from 'axios';
import config from '../config';
import { getBearerToken } from '../util/cookie';
import { userDisplayNameAsString } from '../util/data';
import { notifyError } from '../components/HotToast/HotToastProvider';

// ================ Action types ================ //

// Kept even though it no longer drives any visible UI (the call surface
// moved to the separate Meet app) -- SaaSActiveSessionPage.js still
// dispatches this on session start, and touching that file's auto-open path
// is outside this change's scope.
export const TOGGLE_CALL_DRAWER = 'app/webRTC/TOGGLE_CALL_DRAWER';

export const SET_CALL_INCOMING = 'app/webRTC/SET_CALL_INCOMING';
export const CALL_REJECTED = 'app/webRTC/CALL_REJECTED';
export const SET_CURRENT_REMOTE_USER = 'app/webRTC/SET_CURRENT_REMOTE_USER';
export const RESET_CALL_STATE = 'app/webRTC/RESET_CALL_STATE';

// ================ Reducer ================ //

const initialState = {
  isCallDrawerOpen: false,
  callIncoming: false,
  currentRemoteUser: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case TOGGLE_CALL_DRAWER:
      return {
        ...state,
        isCallDrawerOpen: payload,
      };

    case SET_CALL_INCOMING:
      return {
        ...state,
        callIncoming: payload,
      };

    case CALL_REJECTED:
      return {
        ...state,
        callIncoming: false,
      };

    case SET_CURRENT_REMOTE_USER:
      return {
        ...state,
        currentRemoteUser: {
          ...state.currentRemoteUser,
          ...payload,
        },
      };

    case RESET_CALL_STATE:
      return {
        ...state,
        callIncoming: false,
        currentRemoteUser: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const toggleCallDrawer = isOpen => ({ type: TOGGLE_CALL_DRAWER, payload: isOpen });
export const setIncomingCallState = isIncoming => ({
  type: SET_CALL_INCOMING,
  payload: isIncoming,
});
export const callRejected = () => ({ type: CALL_REJECTED });
export const setCurrentRemoteUser = data => ({ type: SET_CURRENT_REMOTE_USER, payload: data });
export const resetCallState = () => ({ type: RESET_CALL_STATE });

// ================ Thunks ================ //

// The signaling connection has been observed (via server-side connect/
// disconnect logs, correlated live against real call attempts) to drop and
// silently reconnect on its own -- outages have ranged from ~2s up to a
// confirmed 31s -- with no app-level error on either side and no server
// instability. A one-shot 'offer' emitted while the callee's socket happens
// to be mid-reconnect is lost forever: the callee's tab never sees it, and
// the call hangs indefinitely with no feedback that anything went wrong.
// Retrying the SAME invite on an interval, for long enough to ride out even
// the worst outage observed so far, papers over that race. stopRinging()
// (called once the callee's 'answer-call' arrives) ends the retry loop.
const OFFER_RETRY_INTERVAL_MS = 5000;
const OFFER_RETRY_MAX_ATTEMPTS = 12; // 12 * 5s = 60s of retrying before giving up

// The remoteUUID we're currently ringing, if any. Cleared by stopRinging()
// once the callee answers.
let ringingRemoteUUID = null;
export const stopRinging = remoteUUID => {
  if (ringingRemoteUUID === remoteUUID) ringingRemoteUUID = null;
};

// The remoteUUID we most recently answered, and for how long that stays
// "recent". A retry of the caller's invite (see above) can land AFTER we've
// already answered and opened the Meet app in a new tab -- without this,
// socket.duck.js's 'offer' handler would re-show the incoming-call popup on
// top of a call already joined (the exact "ringing again" bug fixed earlier
// in this project). 90s comfortably outlasts the 60s retry window above.
const ANSWERED_COOLDOWN_MS = 90000;
let answeredRemoteUUID = null;
let answeredCooldownTimer = null;
function markAnswered(remoteUUID) {
  answeredRemoteUUID = remoteUUID;
  if (answeredCooldownTimer) clearTimeout(answeredCooldownTimer);
  answeredCooldownTimer = setTimeout(() => {
    answeredRemoteUUID = null;
  }, ANSWERED_COOLDOWN_MS);
}
export const wasRecentlyAnswered = remoteUUID => answeredRemoteUUID === remoteUUID;

function scheduleInviteRetry(getState, remoteUUID, invitePayload, attempt = 0) {
  if (attempt >= OFFER_RETRY_MAX_ATTEMPTS) {
    if (ringingRemoteUUID === remoteUUID) {
      notifyError(
        "Couldn't reach the other participant after a minute of retrying. " +
          'Their connection may be unstable — ask them to check their network and try again.'
      );
    }
    return;
  }
  setTimeout(() => {
    if (ringingRemoteUUID !== remoteUUID) return; // answered / cancelled / torn down
    const { socket } = getState().socket;
    if (socket && !socket.disconnected) {
      socket.emit('offer', remoteUUID, invitePayload);
    }
    scheduleInviteRetry(getState, remoteUUID, invitePayload, attempt + 1);
  }, OFFER_RETRY_INTERVAL_MS);
}

// Deterministic LiveKit room name for a 1:1 call between two users — derived
// independently on both sides from the pair of Sharetribe user uuids, so no
// value needs to be relayed ahead of the call actually connecting.
function buildRoomName(uuidA, uuidB) {
  return `viveka-call-${[uuidA, uuidB].filter(Boolean).sort().join('-')}`;
}

export async function fetchLivekitCredentials(sdk, roomName, participantName) {
  const res = await axios.post(
    `${config.coachSaaSService}client/video-call/livekit-token`,
    { roomName, name: participantName },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
  const data = res && res.data;
  if (!data || !data.url || !data.token) {
    throw new Error('LiveKit token response missing url/token');
  }
  return data;
}

// The standalone Meet app. Defaults to the production/demo shared instance;
// override via REACT_APP_MEET_APP_URL if a per-environment instance is ever
// needed. Port 9443, not the standard 443 -- live.viveka.world's box also
// runs coturn, which already occupies 443 for its own TURN-over-TLS
// fallback (needed by the still-live old call engine); LiveKit itself is on
// :8443 for the same reason. Caddy on that box reverse-proxies this port to
// the Meet app's Cloud Run service.
export const MEET_APP_URL = (
  process.env.REACT_APP_MEET_APP_URL || 'https://live.viveka.world:9443'
).replace(/\/$/, '');

// Builds the join link Viveka Meet's room page accepts a pre-authorized
// token through (see that app's app/rooms/[roomName]/page.tsx +
// PageClientImpl.tsx) -- token/url are already minted server-side and tied
// to this user's verified identity, so Meet can connect straight in without
// its own login/PreJoin step.
export function buildJoinUrl(roomName, token, serverUrl, participantName) {
  const url = new URL(`${MEET_APP_URL}/rooms/${encodeURIComponent(roomName)}`);
  url.searchParams.set('token', token);
  url.searchParams.set('url', serverUrl);
  if (participantName) url.searchParams.set('name', participantName);
  return url.toString();
}

function myIdentity(getState) {
  const { currentUser } = getState().user || {};
  const uuid = (((currentUser || {}).id || {}).uuid) || '';
  const name = userDisplayNameAsString(currentUser, '');
  return { uuid, name };
}

export const offer = remoteUUID => async (dispatch, getState, sdk) => {
  console.log('offer', { remoteUUID });
  dispatch(setCurrentRemoteUser({ id: remoteUUID }));

  // Open the tab synchronously, before the credential fetch below awaits --
  // a window.open() issued after an await has lost the click's user
  // activation, so browsers silently block it (or demote it to a
  // chrome-less popup window instead of a normal tab) rather than opening
  // it outright. Opening blank now and pointing it at the real join URL
  // once we have it keeps this a real, unblocked tab.
  const meetTab = window.open('', '_blank');

  const { uuid: myUuid, name: myName } = myIdentity(getState);
  const roomName = buildRoomName(myUuid, remoteUUID);

  let joinUrl;
  try {
    const { url, token } = await fetchLivekitCredentials(sdk, roomName, myName);
    joinUrl = buildJoinUrl(roomName, token, url, myName);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[webrtc] failed to start call:', e && e.message);
    notifyError('Could not start the video call. Please try again.');
    if (meetTab) meetTab.close();
    return;
  }

  // Open the meeting immediately for the caller (matches the old "camera
  // turns on the moment you click join" immediacy) rather than waiting for
  // the callee to answer first. Own tab on Viveka Meet's own hosting
  // (live.viveka.world), not an in-app popup -- no 'noopener' so
  // window.opener survives and Meet can still post the call summary back.
  if (meetTab) {
    meetTab.location.href = joinUrl;
  } else {
    // meetTab is null if the browser blocked the synchronous open too --
    // try once more anyway so there's still a visible failure to react to.
    window.open(joinUrl, '_blank');
  }

  // Ring the callee over the existing signaling socket. Payload only carries
  // roomName for debuggability — the callee independently derives the same
  // deterministic room name from the two known user uuids.
  ringingRemoteUUID = remoteUUID;
  const invitePayload = { roomName };
  const { socket } = getState().socket;
  if (socket && !socket.disconnected) {
    socket.emit('offer', remoteUUID, invitePayload);
  }
  scheduleInviteRetry(getState, remoteUUID, invitePayload);
};

export const answer = remoteUUID => async (dispatch, getState, sdk) => {
  console.log('answer', { remoteUUID });
  markAnswered(remoteUUID);
  dispatch(setCurrentRemoteUser({ id: remoteUUID }));
  dispatch(setIncomingCallState(false));

  // See the matching comment in offer() above -- open blank now, inside the
  // click handler's call stack, then navigate once the join URL is ready.
  const meetTab = window.open('', '_blank');

  const { uuid: myUuid, name: myName } = myIdentity(getState);
  const roomName = buildRoomName(myUuid, remoteUUID);

  let joinUrl;
  try {
    const { url, token } = await fetchLivekitCredentials(sdk, roomName, myName);
    joinUrl = buildJoinUrl(roomName, token, url, myName);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[webrtc] failed to join call:', e && e.message);
    notifyError('Could not join the video call. Please try again.');
    if (meetTab) meetTab.close();
    return;
  }

  if (meetTab) {
    meetTab.location.href = joinUrl;
  } else {
    window.open(joinUrl, '_blank');
  }

  const { socket } = getState().socket;
  if (socket && !socket.disconnected) {
    socket.emit('answer-call', remoteUUID, { roomName });
  }
};

export const rejectCall = remoteUUID => (dispatch, getState) => {
  const { socket } = getState().socket;
  if (socket) socket.emit('reject-call', remoteUUID);
  dispatch(callRejected());
  dispatch(resetCallState());
};

// Reused by socket.duck.js's 'reject-call'/'disconnect-call' handlers, which
// just need to reset the incoming-call popup state now -- there's no Room/
// media session left in this app to tear down.
export const disconnectAndSetup = () => dispatch => {
  dispatch(resetCallState());
};

// No-op: call-quality telemetry was collected from inside the embedded call
// session, which no longer exists here — the call itself now runs entirely
// in the separate Viveka Meet app. Kept as a function (not deleted) so
// socket.duck.js's existing call sites don't need to change.
export const setEndReason = () => {};

// Hard stop for logout: stop any in-flight ring retry and reset the
// incoming-call popup, then drop the signaling socket. There's no
// Room/media to release here anymore.
export const endCallAndCleanup = () => (dispatch, getState) => {
  const { socket } = getState().socket || {};
  ringingRemoteUUID = null;
  dispatch(resetCallState());
  if (socket) {
    try {
      socket.disconnect();
    } catch (e) {
      /* already disconnected */
    }
  }
};
